import {
  createLinkRelStylesheetTagWithGivenUrl,
  createStyleTagWithRawCssCodeInside,
  getDocumentHead,
  insertBeforeElement
} from "@/util/dom";
import { getHash } from "@/util/hash";

/*
Some plugins may cause the head to end up in the body. So we check both
As long as our PHP code runs, we should always find the meta tag to attach the element to

Details: https://github.com/soflyy/breakdance/pull/4235
 */
function getMetaTagToInsertElementOn(
  canvasDocument: Document,
  metaTagSelector: string
) {
  const elementInHead =
    getDocumentHead(canvasDocument).querySelector(metaTagSelector);

  if (elementInHead) return elementInHead;

  console.warn(
    `Unable to find element in <head> using selector "${metaTagSelector}"`
  );

  return canvasDocument.querySelector(metaTagSelector);
}

export function addOrUpdateUniqueStyleTagBeforeSpecificElementInHead(
  canvasDocument: Document,
  tagId: string,
  rawCssCode: string,
  insertBeforeElFoundBySelector: string
): void {
  const existingTag = canvasDocument.getElementById(tagId);

  if (existingTag) {
    existingTag.innerHTML = rawCssCode;
  } else {
    const newTag = createStyleTagWithRawCssCodeInside(
      canvasDocument,
      rawCssCode,
      tagId
    );

    const insertBeforeEl = getMetaTagToInsertElementOn(
      canvasDocument,
      insertBeforeElFoundBySelector
    );

    if (insertBeforeEl) {
      insertBeforeElement(insertBeforeEl, newTag);
    } else {
      console.error(
        `Unable to find element using selector "${insertBeforeElFoundBySelector}". Page appearance at builder may be corrupted.`
      );
      getDocumentHead(canvasDocument).appendChild(newTag);
    }
  }
}

export function addUniqueLinkRelStylesheetTagBeforeSpecificElementInHead(
  canvasDocument: Document,
  tagId: string,
  stylesheetUrl: string,
  insertBeforeElFoundBySelector: string
): void {
  const existingTag = canvasDocument.getElementById(tagId);

  if (!existingTag) {
    const newTag = createLinkRelStylesheetTagWithGivenUrl(
      canvasDocument,
      stylesheetUrl,
      tagId
    );
    const insertBeforeEl = getMetaTagToInsertElementOn(
      canvasDocument,
      insertBeforeElFoundBySelector
    );

    if (insertBeforeEl) {
      insertBeforeElement(insertBeforeEl, newTag);
    } else {
      console.error(
        `Unable to find element using selector "${insertBeforeElFoundBySelector}". Page appearance at builder may be corrupted.`
      );
      getDocumentHead(canvasDocument).appendChild(newTag);
    }
  }
}

export function appendBreakdanceVersionToUrl(
  url: string,
  breakdanceVersion: string | undefined,
  wpContentUrl: string | undefined
) {
  if (
    !breakdanceVersion ||
    !wpContentUrl ||
    // dev mode
    breakdanceVersion === "%%VERSION%%" ||
    // only modify our own resources
    !url.startsWith(wpContentUrl)
  ) {
    return url;
  }

  // A url may have queries already so we can't just append "?"
  const urlObj = new URL(url);
  urlObj.searchParams.set("bd_ver", breakdanceVersion);

  return urlObj.toString();
}

// Dependencies can't have a hardcoded URL because the 'wp-content' and 'plugin' folders can be customized in WP
export function replaceVariableInDependency(
  url: string,
  elementsPluginUrl?: string | null,
  extraDependencies?: Record<string, string>
) {
  const newUrl = url.replace(/%%BREAKDANCE_REUSABLE_(.+)%%/g, (match, p1) => {
    // Convert snake_case to camelCase
    const key = p1
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group: string) =>
        group.toUpperCase().replace("-", "").replace("_", "")
      );

    if (extraDependencies && extraDependencies[key]) {
      return extraDependencies[key];
    }
    // if no match, return the original string
    return match;
  });

  return elementsPluginUrl
    ? newUrl.replace("%%BREAKDANCE_ELEMENTS_PLUGIN_URL%%", elementsPluginUrl)
    : newUrl;
}

export function addUniqueStylesheetToEndOfHead(
  canvasDocument: Document,
  {
    stylesheetUrl,
    tagIdArg
  }: {
    stylesheetUrl: string;
    tagIdArg?: string;
  }
) {
  const tagId = tagIdArg || getHash(stylesheetUrl);

  if (!canvasDocument.getElementById(tagId)) {
    getDocumentHead(canvasDocument).appendChild(
      createLinkRelStylesheetTagWithGivenUrl(
        canvasDocument,
        stylesheetUrl,
        tagId
      )
    );
  }
}

export function addOrUpdateInlineCSSAtEndOfHead(
  canvasDocument: Document,
  tagIdArg: string | false,
  css: string
) {
  const tagId = tagIdArg || getHash(css);
  const currentTag = canvasDocument.getElementById(tagId);

  if (currentTag) {
    currentTag.innerHTML = css;
  } else {
    getDocumentHead(canvasDocument).appendChild(
      createStyleTagWithRawCssCodeInside(canvasDocument, css, tagId)
    );
  }
}
